import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import APIRequests from '../../../utility/APIRequests';
import Helper1, { notify } from '../../../utility/Helper1';
import { userSelect, allRemoteSettingsSelect, allLocalSettingsSelect } from '../../../redux/selectors/index';
import { authActions } from '../../../redux/actions';

import { WebLink, SiteWide, Images } from "../../../utility/customComponents";
import LoadingIcon from "../../../utility/customComponents/loader/LoadingIcon";
import ReCAPTCHA from "../../../utility/customComponents/ReCAPTCHA";
import { Mail, User, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";

import AuthHolder from "./AuthHolder";

const Signin = () => {
    const dispatch = useDispatch();
    const users = useSelector(userSelect);

    const [email, setEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [loading, changeLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [notificationMessage, setNotificationMessage] = useState('');
    const [fullPageLoading, setFullPageLoading] = useState(false);

    const navigate = useNavigate();

    const site_settings = useSelector(allRemoteSettingsSelect);
    const local_settings = useSelector(allLocalSettingsSelect);

    const [captcha, setCaptcha] = useState(null);

    const captchaHasChanged = (data) => {
        setCaptcha(data);
    }

    const doLogin = async (e) => {
        e.preventDefault();

        if (!captcha && (Number(site_settings?.site_settings?.use_google_reCaptcha) == 1)) {
            notify('Invalid captcha response', 0);
            return;
        }

        setFullPageLoading(true);
        if (loading) { return; }

        changeLoading(true);
        setLoadingText("Authenticating...");

        dispatch(authActions.onLogin("account/login", {
            username: email,
            captcha,
            password: password
        }, response => {
            if (response.code !== 0) {
                if (response.code == 1) {
                    let toThisPlace;
                    if (local_settings?.lastPrivateUrl) {
                        toThisPlace = local_settings?.lastPrivateUrl;
                    } else {
                        toThisPlace = '/';
                    }
                    navigate(toThisPlace, { replace: true });
                } else if (response.code === 2) {
                    navigate('/verify-user', { replace: true });
                }
            }

            notify(response.msg, response.code);
            changeLoading(false);
            setLoadingText("");
            setFullPageLoading(false);
        }));
    };

    let userLoginStyle = site_settings?.operational_settings?.userLoginStyle;
    let userLoginStyleObj = [
        {
            inputType: 'email',
            placeholder: 'Email',
        },
        {
            inputType: 'text',
            placeholder: 'Username',
        }
    ];

    return (
        <AuthHolder>
            <div className="space-y-1 p-6 pb-4 relative z-10">
                <h2 className="text-2xl font-bold text-center text-blue-400">Let’s Get Started</h2>
                <p className="text-center text-slate-400">Provide your account info. below</p>
            </div>

            <div className="p-6 pt-2 relative z-10">
                <form onSubmit={doLogin} className="space-y-5">
                    <div className="space-y-2">
                        <label htmlFor="email" className="block text-slate-300 text-sm font-medium">
                            {userLoginStyleObj?.[userLoginStyle]?.placeholder}
                        </label>
                        <div className="relative group">
                            <User className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                            <input
                                id="email"
                                type={userLoginStyleObj?.[userLoginStyle]?.inputType}
                                placeholder={userLoginStyleObj?.[userLoginStyle]?.placeholder}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full pl-10 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                            />
                            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <label htmlFor="password" className="block text-slate-300 text-sm font-medium">
                                Password
                            </label>
                            <WebLink
                                href="/reset"
                                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                Reset password?
                            </WebLink>
                        </div>
                        <div className="relative group">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full pl-10 pr-10 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-slate-200 placeholder-slate-500 transition-all"
                            />

                            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                        </div>
                    </div>


                    <div className="pt-1">
                        <ReCAPTCHA onChange={captchaHasChanged} />
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed mt-2 relative overflow-hidden group"
                        disabled={loading}
                    >
                        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-40 transition-opacity duration-300 transform -translate-x-full group-hover:translate-x-0"></span>
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Signing in...
                            </div>
                        ) : (
                            <span className="relative z-10">Sign in</span>
                        )}
                    </button>
                </form>
            </div>

            <div className="px-6 pb-6 pt-0 relative z-10">
                {/* <div className="relative w-full my-4">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-slate-700" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                        <span className="bg-slate-800 px-2 text-slate-400">or</span>
                    </div>
                </div> */}

                <p className="text-center text-sm text-slate-400">
                   
                    <WebLink
                        to="/signup"
                        className="font-medium text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Register An Account
                    </WebLink>
                </p>
            </div>
        </AuthHolder>
    );
};

export default Signin;